import { TCards } from '@lbs/shared-rp-types'
import { assign, createMachine } from 'xstate'

// Safety fallback: unlock UI if server patch is never received (network drop).
const SUBMITTING_TIMEOUT_MS = 5_000

type Context = {
  cardsForSwitch: TCards[]
  insuranceTemp: number
}

type Event =
  | { type: 'TOGGLE_CARD'; card: TCards }
  | { type: 'SET_SIX_CARD' }
  | { type: 'SET_INSURANCE' }
  | { type: 'CONFIRM' }
  | { type: 'CANCEL' }
  | { type: 'PENDING_CARDS_STARTED' }
  | { type: 'PENDING_CARDS_RESOLVED' }
  | { type: 'SUBMISSION_RESOLVED' }
  | { type: 'ACTION_ERROR' }

export const boxDecisionMachine = createMachine(
  {
    id: 'boxDecision',
    initial: 'idle',
    predictableActionArguments: true,
    schema: {
      context: {} as Context,
      events: {} as Event,
    },
    context: {
      cardsForSwitch: [],
      insuranceTemp: 0,
    },
    states: {
      idle: {
        on: {
          TOGGLE_CARD: {
            target: 'switch',
            actions: 'addCard',
          },
          SET_SIX_CARD: 'sixCard',
          SET_INSURANCE: 'insurance',
          PENDING_CARDS_STARTED: 'pendingCards',
        },
      },

      switch: {
        on: {
          TOGGLE_CARD: [
            {
              // последняя карта снята — возвращаемся в idle
              cond: 'isRemovingLastCard',
              target: 'idle',
              actions: 'clearCards',
            },
            {
              actions: 'toggleCard',
            },
          ],
          CANCEL: {
            target: 'idle',
            actions: 'clearCards',
          },
          // После CONFIRM блокируем UI до ответа сервера
          CONFIRM: 'submitting',
          PENDING_CARDS_STARTED: 'pendingCards',
        },
        exit: 'clearCards',
      },

      sixCard: {
        on: {
          CANCEL: 'idle',
          CONFIRM: 'submitting',
          PENDING_CARDS_STARTED: 'pendingCards',
        },
      },

      insurance: {
        on: {
          CANCEL: 'idle',
          CONFIRM: 'submitting',
          PENDING_CARDS_STARTED: 'pendingCards',
        },
        exit: 'clearInsurance',
      },

      // Транзитный lock: блокирует повторные клики пока не придёт ответ сервера.
      // Выходы:
      //   PENDING_CARDS_STARTED — сервер ответил успехом и ожидает карты (switch/buySixCard)
      //   SUBMISSION_RESOLVED   — сервер ответил успехом без pending-карт (insurance)
      //   ACTION_ERROR          — сервер ответил ошибкой (показ toast уже обработан в store)
      //   after timeout         — аварийный fallback при потере patch (network drop)
      submitting: {
        after: {
          [SUBMITTING_TIMEOUT_MS]: 'idle',
        },
        on: {
          PENDING_CARDS_STARTED: 'pendingCards',
          SUBMISSION_RESOLVED: 'idle',
          ACTION_ERROR: 'idle',
        },
      },

      pendingCards: {
        on: {
          PENDING_CARDS_RESOLVED: 'idle',
        },
      },
    },
  },
  {
    actions: {
      addCard: assign({
        cardsForSwitch: (ctx, event) => {
          if (event.type !== 'TOGGLE_CARD') return ctx.cardsForSwitch
          return [...ctx.cardsForSwitch, event.card]
        },
      }),

      toggleCard: assign({
        cardsForSwitch: (ctx, event) => {
          if (event.type !== 'TOGGLE_CARD') return ctx.cardsForSwitch
          const isSelected = ctx.cardsForSwitch.includes(event.card)
          return isSelected
            ? ctx.cardsForSwitch.filter((c) => c !== event.card)
            : [...ctx.cardsForSwitch, event.card]
        },
      }),

      clearCards: assign({ cardsForSwitch: [] }),

      clearInsurance: assign({ insuranceTemp: 0 }),
    },

    guards: {
      isRemovingLastCard: (ctx, event) => {
        if (event.type !== 'TOGGLE_CARD') return false
        return (
          ctx.cardsForSwitch.length === 1 &&
          ctx.cardsForSwitch.includes(event.card)
        )
      },
    },
  }
)
